import React from "react";
import { useHistory, useLocation } from "react-router-dom";

import { BasicButton, ButtonType } from "@gaming/xds-react-js";

import { OnboardingPageContainer } from "../../components";
import {
  PageType,
  PartnerAccessDeniedCode,
  partnerAccessDeniedErrors,
  partnerQueryParameters,
} from "../../constants";
import { getImage, ImageType } from "../../helpers/imageHelper";
import { useString, useStrings } from "../../localization/localizationHelpers";
import * as styles from "./AccessDeniedPage.module.css";

type AccessDeniedPageProps = Readonly<{
  footNote: string;
  pageType?: PageType;
  code?: PartnerAccessDeniedCode;
}>;

export const AccessDeniedPage = ({
  footNote,
  pageType,
  code = PartnerAccessDeniedCode.Unspecified,
}: AccessDeniedPageProps) => {
  const [oops, accessDenied, back, errorCode] = useStrings(
    "oops",
    "accessDenied",
    "back",
    "accessDeniedErrorCode"
  );
  const history = useHistory();

  return (
    <OnboardingPageContainer className={styles.accessDeniedContainer} pageType={pageType}>
      <div className={styles.columnWrapper}>
        <section className={styles.contentBox} aria-labelledby="access-denied-title">
          <img
            src={getImage("errorpage", ImageType.Png)}
            alt=""
            aria-hidden="true"
            className={styles.image}
          />
          <div className={styles.contentColumn}>
            <p className={styles.errorPage}>{oops}</p>
            <h1 id="access-denied-title">{accessDenied}</h1>
            <p className={styles.pageFootNote}>{footNote}</p>
            <BasicButton
              as={"button"}
              buttonType={ButtonType.Brand}
              className={styles.supportButton}
              onClick={() => history.goBack()}
              type={"button"}
            >
              {back}
            </BasicButton>
            <p className={styles.pageFootNote}>{errorCode.replace("{0}", String(code))}</p>
          </div>
        </section>
      </div>
    </OnboardingPageContainer>
  );
};

export const IdXboxExistingPartnerAccessDeniedPage = () => {
  const idXboxAccessDeniedFootnote = useString("idXboxAccessDeniedPageFootNote");
  return (
    <AccessDeniedPage
      footNote={idXboxAccessDeniedFootnote}
      pageType={PageType.IdXboxApplication}
      code={PartnerAccessDeniedCode.LegacyAccount}
    />
  );
};

export const IdXboxAccessDeniedPage = () => {
  const idXboxAccessDeniedFootnote = useString("accessDeniedPageFootNote");
  return (
    <AccessDeniedPage footNote={idXboxAccessDeniedFootnote} pageType={PageType.IdXboxApplication} />
  );
};

export const PitchAccessDeniedPage = () => {
  const pitchAccessDeniedFootnote = useString("accessDeniedPageFootNote");
  return (
    <AccessDeniedPage footNote={pitchAccessDeniedFootnote} pageType={PageType.PitchApplication} />
  );
};

export const AaaAccessDeniedPage = () => {
  const aaaAccessDeniedPageFootNote = useString("aaaAccessDeniedPageFootNote");
  return (
    <AccessDeniedPage
      footNote={aaaAccessDeniedPageFootNote}
      pageType={PageType.AaaApplication}
      code={PartnerAccessDeniedCode.HubNotEnabled}
    />
  );
};

export const PartnerAccessDeniedPage = () => {
  const location = useLocation();
  const [defaultFootnote, adminFootnote, generalFootnote] = useStrings(
    "partnerAccessDeniedPageFootnote",
    "partnerAdminAccessDeniedPageFootnote",
    "accessDeniedPageFootNote"
  );
  const error = new URLSearchParams(location.search).get(partnerQueryParameters.accessDeniedError);
  const knownCode = Object.values(PartnerAccessDeniedCode).find(
    (value): value is PartnerAccessDeniedCode =>
      typeof value === "number" && String(value) === error
  );
  const code =
    error === partnerAccessDeniedErrors.admin
      ? PartnerAccessDeniedCode.AdministratorRequired
      : knownCode ?? PartnerAccessDeniedCode.Unspecified;
  const footNote =
    code === PartnerAccessDeniedCode.AdministratorRequired
      ? adminFootnote
      : code === PartnerAccessDeniedCode.Unspecified ||
        code === PartnerAccessDeniedCode.SellerInactive ||
        code === PartnerAccessDeniedCode.AppDevInactive
      ? defaultFootnote
      : generalFootnote;

  return <AccessDeniedPage code={code} footNote={footNote} />;
};
